'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Alert, Button, Card, PasswordInput, ScrollArea, Stack, Text, TextInput, Title } from '@mantine/core';

import { USERNAME_MAX_LENGTH, normalizeUsername } from '../../lib/auth/usernames';
import type { ContentLocale } from '../../lib/content/constants';
import { engineStrings } from '../../lib/portal/engine-strings';

/** Matches the server bound, so the client never truncates something it would accept. */
const PASSWORD_MAX_LENGTH = 256;

/**
 * Developer credential form for the Echo Engine portal.
 *
 * Unrelated to /login, which brokers Firebase Auth ID tokens for the JUCE
 * WebView. This form talks only to /api/echoengine/auth/login and never touches
 * the Firebase client SDK.
 */
export default function LoginForm({ redirectTo, locale }: { redirectTo: string; locale: ContentLocale }) {
  const router = useRouter();
  const text = engineStrings(locale);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [usernameError, setUsernameError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // Mirrors what the server rejects outright rather than adding a stricter
    // rule of its own: an empty field and a syntactically impossible username
    // both cost a rate-limit attempt, and neither needs a round trip to know.
    const usernameProblem = username.trim().length === 0
      ? text.usernameRequired
      : normalizeUsername(username) === null
        ? text.usernameMalformed
        : null;
    const passwordProblem = password.length === 0 ? text.passwordRequired : null;

    setUsernameError(usernameProblem);
    setPasswordError(passwordProblem);

    if (usernameProblem || passwordProblem) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch('/api/echoengine/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, next: redirectTo }),
      });

      const payload = (await response.json().catch(() => null)) as
        | { redirectTo?: string; error?: string }
        | null;

      if (!response.ok) {
        setError(payload?.error ?? text.signInFailed);
        setPassword('');
        setSubmitting(false);
        return;
      }

      // The session cookie arrived with the response, so the destination renders
      // authenticated. refresh() drops any cached anonymous render of it.
      router.replace(payload?.redirectTo ?? redirectTo);
      router.refresh();
    } catch {
      setError(text.offline);
      setSubmitting(false);
    }
  }

  return (
    // The app shell sets a fixed main height with overflow hidden, so this page
    // owns its own scroll container instead of growing the document.
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <ScrollArea style={{ flex: 1, minHeight: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '48px 24px' }}>
          <Card
            radius="md"
            padding="lg"
            style={{
              width: '100%',
              maxWidth: 420,
              background: '#23262f',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            {/*
              `method="post"` matters even though submission goes through fetch:
              if hydration ever fails, the browser falls back to a native submit,
              and the default GET would put the password in the URL — in history,
              in the referrer and in the access log.
            */}
            <form onSubmit={handleSubmit} method="post" noValidate>
              <Stack gap="md">
                <div>
                  <Title order={3} c="#ffffff">
                    {text.signInTitle}
                  </Title>
                  <Text size="sm" c="#b8b8b8" mt={4}>
                    {text.signInSubtitle}
                  </Text>
                </div>

                {error ? (
                  <Alert color="red" variant="light" role="alert">
                    {error}
                  </Alert>
                ) : null}

                <TextInput
                  label={text.username}
                  name="username"
                  value={username}
                  onChange={(event) => {
                    setUsername(event.currentTarget.value);
                    setUsernameError(null);
                  }}
                  error={usernameError}
                  autoComplete="username"
                  autoCapitalize="none"
                  spellCheck={false}
                  required
                  maxLength={USERNAME_MAX_LENGTH}
                  disabled={submitting}
                />

                <PasswordInput
                  label={text.password}
                  name="password"
                  value={password}
                  onChange={(event) => {
                    setPassword(event.currentTarget.value);
                    setPasswordError(null);
                  }}
                  error={passwordError}
                  autoComplete="current-password"
                  required
                  maxLength={PASSWORD_MAX_LENGTH}
                  disabled={submitting}
                  visibilityToggleButtonProps={{ tabIndex: 0, 'aria-label': text.togglePassword }}
                />

                <Button type="submit" loading={submitting} fullWidth mt="xs">
                  {text.signIn}
                </Button>
              </Stack>
            </form>
          </Card>
        </div>
      </ScrollArea>
    </div>
  );
}
